import { Link } from 'react-router';
import { ArrowIcon } from '@/components/ui/Button';
import { Photo } from '@/components/ui/Photo';
import { DIVISION_PATH } from '@/features/catalog/api';
import { CATEGORY_PHOTOS } from '@/features/site/media';
import { cn } from '@/lib/utils';
import type { Category } from '@/types/api';

/** Image-led category tile; categories without approved photography get a typographic navy tile. */
export function CategoryTile({ category, featured = false, className }: { category: Category; featured?: boolean; className?: string }) {
  const photo = CATEGORY_PHOTOS[category.slug];
  const to = `${DIVISION_PATH[category.division]}/${category.slug}`;
  const count = `${category.productCount} product${category.productCount === 1 ? '' : 's'}`;

  if (!photo) {
    return (
      <Link
        to={to}
        className={cn(
          'group relative flex flex-col justify-between overflow-hidden rounded-[8px] bg-primary p-6 text-white transition-colors hover:bg-primary-light',
          className,
        )}
      >
        <span className="eyebrow text-steel">{count}</span>
        <div>
          <h3 className="font-display text-[22px] font-bold tracking-tight">{category.name}</h3>
          {category.description && <p className="mt-2 line-clamp-2 text-[14px] leading-relaxed text-steel">{category.description}</p>}
          <span className="mt-4 inline-flex items-center gap-2 text-[13.5px] font-semibold">
            Explore <ArrowIcon />
          </span>
        </div>
      </Link>
    );
  }

  return (
    <Link to={to} className={cn('group relative block overflow-hidden rounded-[8px] bg-primary', className)}>
      <Photo name={photo} zoom alt="" sizes={featured ? '(min-width: 1024px) 50vw, 100vw' : '(min-width: 1024px) 25vw, 50vw'} className="absolute inset-0" />
      <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-primary-dark/85 via-primary-dark/20 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-6 text-white">
        <span className="eyebrow text-white/75">{count}</span>
        <h3 className={cn('mt-1.5 font-display font-bold tracking-tight', featured ? 'text-[28px]' : 'text-[21px]')}>{category.name}</h3>
        {featured && category.description && <p className="mt-2 max-w-[420px] text-[14.5px] leading-relaxed text-white/80">{category.description}</p>}
        <span className="mt-3 inline-flex items-center gap-2 text-[13.5px] font-semibold">
          Explore <ArrowIcon />
        </span>
      </div>
    </Link>
  );
}
